import { useEffect, useState } from "react";
import { Compact, type ColorResult } from "@uiw/react-color";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const DEFAULT_COLOR_PICKER_COLORS = [
  "#1D4ED8",
  "#2563EB",
  "#0EA5E9",
  "#14B8A6",
  "#10B981",
  "#22C55E",
  "#84CC16",
  "#EAB308",
  "#F59E0B",
  "#F97316",
  "#EF4444",
  "#DC2626",
  "#EC4899",
  "#D946EF",
  "#A855F7",
  "#8B5CF6",
  "#111827",
  "#FFFFFF",
];

interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  colors?: string[];
}

export function ColorPicker({
  value,
  onChange,
  disabled = false,
  ariaLabel,
  colors = DEFAULT_COLOR_PICKER_COLORS,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const normalizedValue = value.toUpperCase();

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  const handleCompactChange = (nextColor: ColorResult) => {
    onChange(nextColor.hex.toUpperCase());
  };

  return (
    <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className="inline-flex size-5 overflow-hidden rounded-sm border border-border bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className="inline-block size-full"
            style={{
              backgroundColor: normalizedValue,
              border:
                normalizedValue === "#FFFFFF"
                  ? "1px solid hsl(var(--border))"
                  : "1px solid transparent",
            }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-auto rounded-md border bg-popover p-2 shadow-md">
        <Compact
          prefixCls="ca-color-compact"
          color={normalizedValue}
          colors={colors}
          onChange={handleCompactChange}
          rectProps={{
            style: {
              width: 18,
              height: 18,
              marginRight: 4,
              marginBottom: 4,
              borderRadius: 4,
              border: "1px solid hsl(var(--border))",
            },
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
